import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ApiService } from '../../../_core/_services/api.service';
import { CountryCurrencyDto } from '../../../_core/_model/countries';
import { ComplianceTierDetailsDto } from '../../../_core/_model/compliance';

@Component({
  selector: 'app-admin-tier-limits',
  standalone: false,
  templateUrl: './admin-tier-limits.html',
  styleUrl: './admin-tier-limits.css',
})
export class AdminTierLimits implements OnInit {
  loading = false;
  loadingCurrencies = false;
  saving = false;
  deletingId: string | null = null;
  error: string | null = null;

  tierId = '';
  tier: ComplianceTierDetailsDto | null = null;
  limits: any[] = [];
  currencyOptions: CountryCurrencyDto[] = [];

  form!: FormGroup;
  editingId: string | null = null;

  activityTypes = [
    { label: 'Deposit', value: 1 },
    { label: 'Withdrawal', value: 2 },
    { label: 'Trade', value: 3 }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private api: ApiService,
    private notifyService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.tierId = this.route.snapshot.paramMap.get('id') || '';

    this.form = this.fb.group({
      activityType: [1, [Validators.required]],
      currency: ['', [Validators.required, Validators.maxLength(10)]],
      perTransactionLimit: [null],
      dailyLimit: [null],
      monthlyLimit: [null],
      isUnlimited: [false],
      isAllowed: [true]
    });

    if (!this.tierId) {
      this.error = 'Tier not found.';
      return;
    }

    this.loadTier();
    this.loadLimits();
  }

  loadTier(): void {
    this.adminService.getTierById(this.tierId).subscribe({
      next: (res: any) => {
        this.tier = res?.data || res || null;
        this.loadJurisdictionCurrencies();
      },
      error: () => {
        this.tier = null;
        this.currencyOptions = [];
      }
    });
  }

  loadJurisdictionCurrencies(): void {
    const jurisdictionCode = this.tier?.jurisdictionCode;

    this.currencyOptions = [];
    this.form.patchValue({ currency: '' });

    if (!jurisdictionCode) return;

    this.loadingCurrencies = true;

    this.api.getCurrencyCountry(jurisdictionCode, 1, 1000)
      .pipe(finalize(() => (this.loadingCurrencies = false)))
      .subscribe({
        next: (res) => {
          this.currencyOptions = (res.data || []).filter(item => !!item.currency?.code);

          if (this.currencyOptions.length === 1 && !this.editingId) {
            this.form.patchValue({ currency: this.currencyOptions[0].currency.code });
          }
        },
        error: (err) => {
          this.currencyOptions = [];
          this.notifyService.showError(
            err?.error?.message || `Failed to load currencies for ${jurisdictionCode}.`,
            'Error'
          );
        }
      });
  }

  loadLimits(): void {
    this.loading = true;
    this.error = null;

    this.adminService.getTierLimits(this.tierId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.limits = res?.data || res || [];
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load tier limits.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.saving = true;
    this.error = null;

    const payload = {
      activityType: Number(this.form.value.activityType),
      currency: String(this.form.value.currency || '').trim().toUpperCase(),
      perTransactionLimit: this.asNullableNumber(this.form.value.perTransactionLimit),
      dailyLimit: this.asNullableNumber(this.form.value.dailyLimit),
      monthlyLimit: this.asNullableNumber(this.form.value.monthlyLimit),
      isUnlimited: !!this.form.value.isUnlimited,
      isAllowed: !!this.form.value.isAllowed
    };

    const request$ = this.editingId
      ? this.adminService.updateTierLimit(this.editingId, payload)
      : this.adminService.createTierLimit(this.tierId, payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess(
            this.editingId ? 'Tier limit updated successfully.' : 'Tier limit created successfully.',
            'Success'
          );
          this.resetForm();
          this.loadLimits();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save tier limit.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  edit(item: any): void {
    this.editingId = item.id;

    this.form.patchValue({
      activityType: item.activityType,
      currency: item.currency || '',
      perTransactionLimit: item.perTransactionLimit,
      dailyLimit: item.dailyLimit,
      monthlyLimit: item.monthlyLimit,
      isUnlimited: item.isUnlimited ?? false,
      isAllowed: item.isAllowed ?? true
    });
  }

  delete(item: any): void {
    if (!item?.id) return;

    this.deletingId = item.id;
    this.error = null;

    this.adminService.deleteTierLimit(item.id)
      .pipe(finalize(() => (this.deletingId = null)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess('Tier limit deleted successfully.', 'Success');
          if (this.editingId === item.id) {
            this.resetForm();
          }
          this.loadLimits();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to delete tier limit.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  resetForm(): void {
    this.editingId = null;
    this.form.reset({
      activityType: 1,
      currency: '',
      perTransactionLimit: null,
      dailyLimit: null,
      monthlyLimit: null,
      isUnlimited: false,
      isAllowed: true
    });
  }

  cancel(): void {
    this.router.navigate(['/admin/compliance/tiers']);
  }

  getActivityTypeLabel(value: number): string {
    return this.activityTypes.find(x => x.value === value)?.label || String(value);
  }

  currencyLabel(item: CountryCurrencyDto): string {
    const currency = item.currency;
    const code = currency?.code || '';
    const name = currency?.name || '';
    const symbol = currency?.symbol ? ` (${currency.symbol})` : '';

    return `${code}${name ? ` - ${name}` : ''}${symbol}`;
  }

  asYesNo(value: any): string {
    return value ? 'Yes' : 'No';
  }

  badgeClass(value: any): string {
    return value ? 'text-bg-success' : 'text-bg-secondary';
  }

  asNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  get activityType() { return this.form.get('activityType'); }
  get currency() { return this.form.get('currency'); }
}
